import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Car, Train, FileText, ExternalLink, Shield, ScrollText } from 'lucide-react';
import axios from 'axios';
import { Skeleton } from '../components/ui/skeleton';
import { getLocalizedPath } from '../utils/routes';
import { useTranslations } from '../context/TranslationsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const { td } = useTranslations();
  const lang = i18n.language?.split('-')[0] || 'pl';
  const [contactInfo, setContactInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper for localized paths
  const lp = (key) => getLocalizedPath(key, lang);
  
  // Smart translate: database first, fallback
  const tr = (key, fallback = null) => {
    const dbValue = td(key);
    if (dbValue === key) return t(key, fallback || key);
    return dbValue;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contactRes, docsRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/documents`)
      ]);
      setContactInfo(contactRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Icon mapping for documents
  const getDocIcon = (slug) => {
    // Handle both string (legacy) and object (multilingual) slug formats
    const slugStr = typeof slug === 'object' ? (slug?.pl || slug?.en || '') : (slug || '');
    if (slugStr.includes('prywatnosc') || slugStr.includes('privacy')) return Shield;
    if (slugStr.includes('skatepark')) return ScrollText;
    return FileText;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Skeleton className="h-12 w-48 mx-auto mb-4" />
            <Skeleton className="h-6 w-72 mx-auto" />
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Skeleton className="h-64 rounded-2xl" />
              <Skeleton className="h-48 rounded-2xl" />
            </div>
            <Skeleton className="h-96 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12" data-testid="contact-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-['Syne'] font-bold mb-3 sm:mb-4">{td('contact.title')}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {td('contact.subtitle', lang === 'pl' ? 'Skontaktuj się z nami lub odwiedź nas osobiście' : 'Contact us or visit us in person')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Contact Info */}
          <div className="space-y-4 sm:space-y-6">
            {/* Address & Phone */}
            <div className="glass-card p-5 sm:p-8">
              <div className="space-y-5 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{td('contact.address')}</h3>
                    <p className="text-muted-foreground">
                      {contactInfo?.address?.street}<br />
                      {contactInfo?.address?.postalCode} {contactInfo?.address?.city}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{td('contact.phone')}</h3>
                    <a href={`tel:${contactInfo?.phone}`} className="text-muted-foreground hover:text-primary transition-colors">
                      {contactInfo?.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{td('contact.email')}</h3>
                    <a href={`mailto:${contactInfo?.email}`} className="text-muted-foreground hover:text-primary transition-colors">
                      {contactInfo?.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="glass-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-primary" />
                <h3 className="text-xl font-bold">{td('contact.hours')}</h3>
              </div>
              <div className="space-y-3">
                {contactInfo?.openingHours?.schedule ? (
                  // Full schedule from admin panel
                  <>
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                      const hours = contactInfo.openingHours.schedule[day];
                      const dayNames = {
                        monday: td('days.monday'),
                        tuesday: td('days.tuesday'),
                        wednesday: td('days.wednesday'),
                        thursday: td('days.thursday'),
                        friday: td('days.friday'),
                        saturday: td('days.saturday'),
                        sunday: td('days.sunday')
                      };
                      if (!hours) return null;
                      return (
                        <div key={day} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                          <span className="text-muted-foreground">{dayNames[day]}</span>
                          <span className={`font-medium ${hours.closed ? 'text-red-400' : ''}`}>
                            {hours.closed 
                              ? td('contact.parkClosed')
                              : `${hours.open} - ${hours.close}`
                            }
                          </span>
                        </div>
                      );
                    })}
                    {/* Show schedule period info if not standard schedule */}
                    {contactInfo.openingHours.scheduleInfo && (
                      <div className="mt-4 pt-4 border-t border-white/10 text-sm text-muted-foreground">
                        {`${td('contact.schedule.from')} ${new Date(contactInfo.openingHours.scheduleInfo.startDate).toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US')} ${td('contact.schedule.to')} ${new Date(contactInfo.openingHours.scheduleInfo.endDate).toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US')}`}
                      </div>
                    )}
                  </>
                ) : (
                  // Fallback simple format
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{td('contact.weekday')}</span>
                      <span className="font-medium">
                        {contactInfo?.openingHours?.weekday?.open} - {contactInfo?.openingHours?.weekday?.close}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{td('contact.weekend')}</span>
                      <span className="font-medium">
                        {contactInfo?.openingHours?.weekend?.open} - {contactInfo?.openingHours?.weekend?.close}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* How to get here */}
            <div className="glass-card p-8">
              <h3 className="text-xl font-bold mb-6">{td('contact.howToReach', t('contact.howToGet'))}</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <Car className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium mb-1">{td('contact.byCar.title', t('contact.byCar'))}</p>
                    <p className="text-sm text-muted-foreground">
                      {td('contact.byCar.desc', lang === 'pl' 
                        ? 'Bezpłatny parking dla gości. Wjazd od ul. Sportowej.'
                        : 'Free parking for guests. Entrance from Sportowa street.')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Train className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium mb-1">{td('contact.byPublic.title', t('contact.byTransit'))}</p>
                    <p className="text-sm text-muted-foreground">
                      {td('contact.byPublic.desc', lang === 'pl'
                        ? 'Tramwaje: 4, 15, 18 - przystanek "Skatepark". Autobus: 125, 175.'
                        : 'Trams: 4, 15, 18 - "Skatepark" stop. Bus: 125, 175.')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map & Documents */}
          <div className="space-y-6">
            {/* Interactive Map */}
            <div className="glass-card p-3">
              <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2443.5!2d21.0122!3d52.2297!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTLCsDEzJzQ2LjkiTiAyMcKwMDAnNDQuMCJF!5e0!3m2!1spl!2spl!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="AvePark Location"
                  data-testid="contact-map"
                  className="w-full h-full"
                />
              </div>
              {/* Map Action Buttons - below map */}
              <div className="flex gap-3 mt-3">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${contactInfo?.coordinates?.lat || 52.2297},${contactInfo?.coordinates?.lng || 21.0122}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-3 bg-primary text-white text-sm font-medium rounded-xl shadow-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="navigate-button"
                >
                  <Car className="w-4 h-4" />
                 {td('contact.map.navigate')}
                </a>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${contactInfo?.coordinates?.lat || 52.2297},${contactInfo?.coordinates?.lng || 21.0122}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-3 bg-white/10 dark:bg-white/10 backdrop-blur-sm text-gray-900 dark:text-white text-sm font-medium rounded-xl hover:bg-white/20 dark:hover:bg-white/20 transition-colors flex items-center justify-center gap-2 border border-white/10"
                  data-testid="open-maps-button"
                >
                  <ExternalLink className="w-4 h-4" />
                  {td('contact.map.openInMaps')}
                </a>
              </div>
            </div>

            {/* Skatepark Rental */}
            <div className="glass-card p-8 mb-6">
              <h3 className="text-xl font-bold mb-4">{td('contact.rental.title')}</h3>
              <p className="text-muted-foreground mb-4">
                {td('contact.rental.desc')}
              </p>
              <a
                href={`mailto:${contactInfo?.email}?subject=Wynajem skateparku`}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="rental-button"
              >
                <Mail className="w-5 h-5" />
                {td('contact.rental.button')}
              </a>
            </div>

            {/* Documents */}
            <div className="glass-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-primary" />
                <h3 className="text-xl font-bold">{td('contact.documents')}</h3>
              </div>
              <div className="flex flex-col gap-3">
                {/* All documents come from API - filtered by showInContact=true */}
                {documents.filter(doc => doc.showInContact).map((doc) => {
                  const DocIcon = getDocIcon(doc.slug);
                  // Handle both string (legacy) and object (multilingual) slug formats
                  const docSlug = typeof doc.slug === 'object' ? (doc.slug?.[lang] || doc.slug?.pl) : doc.slug;
                  return (
                    <Link
                      key={doc.document_id || docSlug}
                      to={lp(`document/${docSlug}`)}
                      className="flex items-center justify-between p-4 bg-black/5 dark:bg-white/5 rounded-xl hover:bg-black/10 dark:hover:bg-white/10 transition-colors border border-transparent hover:border-primary/20"
                      data-testid={`document-${docSlug}`}
                    >
                      <div className="flex items-center gap-3">
                        <DocIcon className="w-5 h-5 text-primary" />
                        <span className="text-foreground">{doc.title?.[lang] || doc.title?.pl}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </Link>
                  );
                })}
                {documents.filter(doc => doc.showInContact).length === 0 && (
                  <p className="text-muted-foreground text-sm">
                    {t('contact.noDocuments', 'Brak dokumentów do wyświetlenia')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
